import { HttpEvent, HttpHandlerFn, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';

import { adaptApiError, AppError, DISABLE_ERROR_INTERCEPTOR } from '@hglobal/fin-ui';
import { catchError, Observable, throwError } from 'rxjs';

import { isBusinessApiUrl } from '@zeusm/shared/core/helpers';
import { GlobalDialogService } from '@zeusm/shared/core/services';
import { AuthService } from '@zeusm/shared/session/auth';

import { ContactSupportDialog, ContactSupportDialogComponent, VpnCheckDialogComponent } from '../../../common';
import { RouterStore } from '../../../store';

interface ErrorFields {
  codeField: string;
  messageField: string;
  dataField: string;
}

const PERSONAL_API_ERROR_FIELDS: ErrorFields = {
  codeField: 'code',
  messageField: 'message',
  dataField: 'details',
};

const BUSINESS_API_ERROR_FIELDS: ErrorFields = {
  codeField: 'error_code',
  messageField: 'error_description',
  dataField: 'details',
};

/** One VPN check dialog at a time. Same lifetime as the old useClass interceptor. */
@Injectable({ providedIn: 'root' })
class VpnCheckDialogState {
  shown = false;
}

export function appErrorsInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  if (request.context.get(DISABLE_ERROR_INTERCEPTOR)) {
    return next(request);
  }

  const injector = inject(Injector);
  const routerStore = inject(RouterStore);
  const dialogService = inject(GlobalDialogService);
  const contactSupportDialog = inject(ContactSupportDialog);
  const vpnDialogState = inject(VpnCheckDialogState);
  const errorFields = isBusinessApiUrl(request.url) ? BUSINESS_API_ERROR_FIELDS : PERSONAL_API_ERROR_FIELDS;

  return next(request).pipe(
    adaptApiError(errorFields),
    catchError((error: AppError) =>
      throwError(() => beforeThrowing(error, injector, routerStore, dialogService, contactSupportDialog, vpnDialogState)),
    ),
  );
}

function beforeThrowing(
  error: AppError,
  injector: Injector,
  routerStore: InstanceType<typeof RouterStore>,
  dialogService: GlobalDialogService,
  contactSupportDialog: ContactSupportDialog,
  vpnDialogState: VpnCheckDialogState,
): AppError {
  const status = error.originalError?.status;
  const url = error.originalError?.url || '';

  if (status === HttpStatusCode.Unauthorized) {
    injector.get(AuthService).clearAuthState();
  }

  if (status === HttpStatusCode.NotFound) {
    routerStore.navigateTo404();
  }

  if (status === HttpStatusCode.Forbidden) {
    if (url.includes('/backoffice/')) {
      showVpnCheckDialog(dialogService, vpnDialogState);
    }

    if (url.includes('/me')) {
      contactSupportDialog.open(ContactSupportDialogComponent, {
        data: { message: 'shared.core.accountNotReady.text' },
      });
    }

    if (error?.code === 'INACTIVE_USER' || error?.code === 'USER_NOT_FOUND') {
      const message = error.code === 'INACTIVE_USER' ? 'shared.core.accountInactive.text' : 'shared.core.accountNotReady.text';

      contactSupportDialog.open(ContactSupportDialogComponent, {
        data: { message },
      });
      injector.get(AuthService).clearAuthState();
    }
  }

  return error;
}

function showVpnCheckDialog(dialogService: GlobalDialogService, vpnDialogState: VpnCheckDialogState): void {
  if (vpnDialogState.shown) {
    return;
  }

  vpnDialogState.shown = true;

  dialogService
    .open<VpnCheckDialogComponent, void>({
      components: [VpnCheckDialogComponent],
      isOnTop: true,
    })
    .afterClosed()
    .subscribe(() => {
      vpnDialogState.shown = false;
    });
}
